/* ============================================================
   ZNAKI · figures.js
   Słownik figur i ich zachowań.

   FIGURY CIĘŻKIE — pomarańczowe koła, czarne belki (i para
   "koło z belką"). Poruszają się z płaszczyzną tablicy:
   płynnie, z lekkim rozwarstwieniem parallax i powolną
   reorientacją. Bez gwałtownych ruchów.

   FIGURY LEKKIE — linie, drobne belki, kropki, kliny, panele
   widmowe. Podlegają trzem fazom podróży:
     Faza 1 · wjazd spoza kadru (dół) i ustawienie
     Faza 2 · trwanie w kompozycji (ruch własny wg osobowości)
     Faza 3 · wyjazd poza kadr (góra)

   Geometria w jednostkach względnych:
     xu, dxVm, rozmiary — vmin;  yv — vh;  dyVm — vmin.

   ZDARZENIA DLA DŹWIĘKU — evaluate() zwraca na specyfikacji
   rysowania znaczniki, z których korzysta audio/accents.js:
     tik   — figura gwałtowna właśnie drgnęła (nowy skok pozy)
     exit  — figura zaczęła schodzić ze sceny, zniknie za chwilę;
             przy scrollu w dół jest to wejście w fazę 3, przy scrollu
             w górę — cofnięcie fazy 1 (patrz `leaving` w evaluate)
     wave  — figura powolna faluje: znacznik trwa przez cały jej pobyt
             na scenie (`fire` tylko w klatce startu), bo pod spodem gra
             jeden przeciągły dźwięk, gaszony przy wyjściu figury
   Liczą się tu same przejścia, więc stan trzymamy przy figurze
   (_tikStep, _exitFired, _waveFired, _settled) i zerujemy go, gdy
   figura opuszcza zasięg.

   Tik i fala to dwa bieguny tej samej skali: tik gra pod gwałtownym
   drganiem (`violent`), fala — pod powolnym kołysaniem (`drift`,
   `calm`). Osobowości pośrednie (`jitter`, `spin`, `pulse`) milczą
   w obu bankach.

   GLITCH — trzeci znacznik: duże pomarańczowe tarcze o środku
   widocznym w kadrze co jakiś czas rozdziera na chwilę zakłócenie
   sygnału (rysuje je renderer.js). Specyfikacja niesie wtedy pole
     glitch: { seed, t, dur, slices, fire }
   gdzie `fire` jest prawdą w klatce startu, a `dur` to wylosowany
   czas trwania [s]. Oba pola czyta audio/accents.js: pod `fire`
   startuje próbkę z banku glitcha, przyciętą dokładnie do `dur`.
   ============================================================ */

import { PARAMS } from './config';
import * as U from './utils';

/* --- osobowości figur lekkich ------------------------------ */
/* amp/speed — ruch własny; inDur/outDur — czas faz 1 i 3 [s]
   (całość w umownym przedziale 0.2–1.5 s) */
const PERSONALITIES = {
  calm:    { amp: 0.5,  speed: 0.5,  inDur: [0.80, 1.50], outDur: [0.70, 1.30], distMul: 0.80 },
  drift:   { amp: 2.1,  speed: 0.32, inDur: [0.70, 1.40], outDur: [0.60, 1.20], distMul: 0.90 },
  jitter:  { amp: 0.7,  speed: 6.0,  inDur: [0.30, 0.70], outDur: [0.25, 0.60], distMul: 1.00 },
  violent: { amp: 1.35, speed: 2.2,  inDur: [0.20, 0.45], outDur: [0.20, 0.40], distMul: 1.15 },
  spin:    { amp: 0.4,  speed: 0.6,  inDur: [0.40, 1.00], outDur: [0.35, 0.90], distMul: 1.00 },
  pulse:   { amp: 0.45, speed: 0.8,  inDur: [0.40, 1.00], outDur: [0.35, 0.90], distMul: 0.95 },
};

/* osobowości grające "falę" — powolne kołysanie, przeciwieństwo
   gwałtownego drgania spod tików; o tym, czy taka figura ma dla
   siebie próbkę, decyduje już bank (audio/banks.js) */
const WAVING = { drift: true, calm: true };

/* ============================================================
   GENERACJA
   ============================================================ */

function baseLight(rng, pers) {
  const P = PERSONALITIES[pers];
  const side = U.sign(rng);
  return {
    kind: 'light',
    pers,
    seed: Math.floor(rng() * 1e6),
    rank: rng(),                          /* maska gęstości (na żywo) */
    depth: (rng() * 2 - 1),               /* rozwarstwienie parallax */
    dyVm: 0,
    inDur:  U.rr(rng, P.inDur[0],  P.inDur[1]),   /* czas fazy 1 [s] */
    outDur: U.rr(rng, P.outDur[0], P.outDur[1]),  /* czas fazy 3 [s] */
    easeJit: U.rr(rng, 0.75, 1.25),               /* wariacja krzywej wjazdu */
    ph: {
      distMul: P.distMul * U.rr(rng, 0.7, 1.3),
      entryDx: side * U.rr(rng, 0.04, 0.22),
      entryRot: (rng() - 0.5) * 0.7,
      exitDx: -side * U.rr(rng, 0.02, 0.18),
      exitRot: (rng() - 0.5) * 0.9,
    },
    idle: { amp: P.amp * U.rr(rng, 0.7, 1.3), speed: P.speed * U.rr(rng, 0.8, 1.25) },
    spinV: pers === 'spin' ? U.sign(rng) * U.rr(rng, 0.10, 0.38) : 0,
    pulse: pers === 'pulse' ? { amp: U.rr(rng, 0.04, 0.10), f: U.rr(rng, 0.5, 1.4) } : null,
  };
}

function lightColor(rng, kindPairs) {
  /* zwraca {color, blend, alpha} wg słownika wizualnego */
  const c = U.rw(rng, kindPairs);
  if (c === 'taupe')  return { color: 'taupe', blend: 'multiply',    alpha: U.rr(rng, 0.36, 0.6) };
  if (c === 'white')  return { color: 'white', blend: 'source-over', alpha: U.rr(rng, 0.42, 0.9) };
  if (c === 'orange') return { color: 'orange', blend: 'source-over', alpha: 1 };
  return { color: 'black', blend: 'source-over', alpha: 1 };
}

/* --- figury ciężkie ---------------------------------------- */

/* gapPrev/gapNext [vh] — odstępy do sąsiadów w kręgosłupie;
   ograniczają promień koła tak, by pomarańcze się nie zlewały */
export function makeHeavy(seed, k, yv, sideAmp, gapPrev, gapNext) {
  const rng = U.rngFor(seed, 'heavy', k);
  const sideSign = (((k % 2) + 2) % 2 === 0) ? 1 : -1;
  const xu = sideSign * U.rr(rng, 0.45, 1.0) * sideAmp;
  const g = Math.min(gapPrev, gapNext);
  /* maska gęstości ciężkich — wspólna dla pary "koło z belką";
     kompozycja otwierająca (k=0) nigdy nie znika */
  const rank = (k === 0) ? 0 : rng();

  const roll = rng();
  const type = (k === 0) ? 'capped'
    : (roll < 0.50 ? 'circle' : (roll < 0.86 ? 'bar' : 'capped'));

  const figs = [];

  const circle = () => {
    const giant = rng() < 0.13 && g > 0.41;
    const r = giant
      ? U.rr(rng, 0.24, 0.60 * g + 0.05)
      : U.rr(rng, 0.12, Math.min(0.23, 0.62 * g));
    return {
      kind: 'heavy', shape: 'circle', color: 'orange',
      blend: 'source-over', alpha: 1, rank,
      xu, yv, dyVm: 0, rot: 0,
      size: { r },
      z: 0.46 + rng() * 0.08,
      depth: (rng() * 2 - 1),
      spin: 0,
      sway: U.rr(rng, 0.006, 0.02),
      seed: Math.floor(rng() * 1e6),
      /* stan glitcha — patrz evaluate(); 0 = tarcza czysta */
      _glDur: 0, _glT: 0, _glSeed: 0, _glSlices: 0, _glFire: false,
    };
  };

  const bar = (over) => {
    const w = U.rr(rng, 0.34, 0.60);
    const h = U.rr(rng, 0.055, 0.09);
    return {
      kind: 'heavy', shape: 'bar', color: 'black',
      blend: 'source-over', alpha: 1, rank,
      xu: xu + (over ? U.rr(rng, -0.04, 0.04) : 0),
      yv, dyVm: 0,
      rot: (rng() < 0.55 ? -1 : 1) * U.rr(rng, 0.10, 0.45),
      size: { w, h },
      z: 0.52 + rng() * 0.08,
      depth: (rng() * 2 - 1),
      spin: U.sign(rng) * U.rr(rng, 0.015, 0.05), /* rad na 1 vh scrolla */
      sway: U.rr(rng, 0.004, 0.014),
      seed: Math.floor(rng() * 1e6),
    };
  };

  if (type === 'circle') {
    figs.push(circle());
  } else if (type === 'bar') {
    figs.push(bar(false));
  } else {
    /* "koło z belką" — sygnaturowy motyw z wizualizacji */
    const c = circle();
    const b = bar(true);
    b.size.w = U.clamp(c.size.r * U.rr(rng, 2.1, 3.1), 0.3, 0.72);
    b.size.h = U.rr(rng, 0.05, 0.078);
    b.dyVm = -c.size.r * U.rr(rng, 0.72, 1.02);   /* belka wieńczy koło */
    b.rot = (rng() < 0.5 ? -1 : 1) * U.rr(rng, 0.06, 0.3);
    b.z = c.z + 0.08;                              /* belka nad kołem */
    figs.push(c, b);
  }
  return figs;
}

/* --- satelity: figury lekkie dostawiane do ciężkich --------- */

export function makeSatellites(seed, k, heavies, satMax) {
  const rng = U.rngFor(seed, 'sat', k);
  const anchor = heavies[0];
  const isCircle = anchor.shape === 'circle';
  const aR = isCircle ? anchor.size.r : Math.max(anchor.size.w, anchor.size.h) * 0.5;

  const count = 4 + Math.floor(rng() * Math.max(1, satMax - 3)); /* 4..satMax */
  const out = [];

  /* talia relacji z limitami — duże figury widmowe (panel, smuga)
     najwyżej raz przy jednej ciężkiej, inaczej kadr się "pierze" */
  const caps = { cross: 3, tangent: 2, dot: 3, panel: 1, shard: 2, beam: 1 };
  const used = {};
  const rels = ['cross', 'dot'];   /* zawsze: linia + kropka (relacje bazowe) */
  used.cross = 1; used.dot = 1;
  for (let a = 0; a < 60 && rels.length < count; a++) {
    const r = U.rw(rng, [
      ['cross', 2.4],    /* długa linia przez / obok figury  */
      ['tangent', 2.2],  /* mała belka na krawędzi / echo    */
      ['dot', 2.0],      /* kropka-satelita                  */
      ['panel', 1.6],    /* widmowy panel w tle              */
      ['shard', 1.7],    /* klin                             */
      ['beam', 1.1],     /* szeroka biała smuga              */
    ]);
    if ((used[r] || 0) >= caps[r]) continue;
    used[r] = (used[r] || 0) + 1;
    rels.push(r);
  }

  for (let i = 0; i < rels.length; i++) {
    const rel = rels[i];

    let f = null;

    if (rel === 'cross') {
      f = baseLight(rng, U.rw(rng, [['jitter', 3], ['violent', 2.4], ['calm', 1.8]]));
      const epic = rng() < 0.12;
      Object.assign(f, {
        shape: 'line',
        ...lightColor(rng, [['black', 0.64], ['white', 0.36]]),
        xu: anchor.xu + (rng() - 0.5) * 0.12,
        yv: anchor.yv, dyVm: (rng() - 0.5) * 0.16,
        rot: U.sign(rng) * U.rr(rng, 0.28, 0.62),
        size: { w: epic ? U.rr(rng, 0.9, 1.35) : U.rr(rng, 0.45, 0.85), h: U.rr(rng, 0.0035, 0.006) },
        z: rng() < 0.3 ? U.rr(rng, 0.26, 0.4) : U.rr(rng, 0.62, 0.88),
      });
    }

    else if (rel === 'tangent') {
      f = baseLight(rng, U.rw(rng, [['calm', 2.4], ['jitter', 1.6], ['violent', 1.2]]));
      if (isCircle) {
        /* belka styczna do krawędzi koła */
        const a = rng() * U.TAU;
        const d = aR * U.rr(rng, 1.0, 1.25);
        Object.assign(f, {
          shape: 'bar',
          ...lightColor(rng, [['black', 0.5], ['white', 0.28], ['orange', 0.22]]),
          xu: anchor.xu + Math.cos(a) * d,
          yv: anchor.yv, dyVm: Math.sin(a) * d,
          rot: a + Math.PI / 2 + (rng() - 0.5) * 0.24,
          size: { w: U.rr(rng, 0.09, 0.2), h: U.rr(rng, 0.016, 0.034) },
          z: anchor.z + U.rr(rng, 0.06, 0.22),
        });
      } else {
        /* równoległe echo belki */
        const off = U.sign(rng) * U.rr(rng, 0.07, 0.15);
        Object.assign(f, {
          shape: 'bar',
          ...lightColor(rng, [['black', 0.4], ['white', 0.4], ['orange', 0.2]]),
          xu: anchor.xu + Math.sin(anchor.rot) * off * 1.2,
          yv: anchor.yv, dyVm: Math.cos(anchor.rot) * off,
          rot: anchor.rot + (rng() - 0.5) * 0.14,
          size: { w: anchor.size.w * U.rr(rng, 0.35, 0.7), h: U.rr(rng, 0.014, 0.03) },
          z: anchor.z + (rng() < 0.5 ? -0.18 : 0.12),
        });
      }
    }

    else if (rel === 'dot') {
      f = baseLight(rng, U.rw(rng, [['drift', 2.4], ['pulse', 2.0], ['calm', 1.4]]));
      const a = rng() * U.TAU;
      const col = lightColor(rng, [['orange', 1.1], ['taupe', 1.7]]);
      const r = U.rr(rng, 0.024, 0.062);
      /* taupe może leżeć NA kole (multiply rysuje piękny cień);
         pomarańcz musi wyjść poza sylwetę, inaczej zlewa się z kołem */
      let d;
      if (isCircle) {
        d = col.color === 'orange'
          ? U.rr(rng, aR + r * 1.5, Math.max(aR + r * 1.6, aR * 1.75))
          : aR * U.rr(rng, 0.45, 1.6);
      } else {
        d = anchor.size.w * U.rr(rng, 0.45, 0.75);
      }
      Object.assign(f, {
        shape: 'circle',
        ...col,
        xu: anchor.xu + Math.cos(a) * d,
        yv: anchor.yv, dyVm: Math.sin(a) * d * 0.9,
        rot: 0,
        size: { r },
        z: U.rr(rng, 0.6, 0.92),
      });
    }

    else if (rel === 'panel') {
      f = baseLight(rng, U.rw(rng, [['calm', 2.6], ['drift', 2.0]]));
      Object.assign(f, {
        shape: 'bar',
        ...lightColor(rng, [['taupe', 0.62], ['white', 0.38]]),
        xu: anchor.xu + (rng() - 0.5) * 0.5,
        yv: anchor.yv, dyVm: (rng() - 0.5) * 0.36,
        rot: U.sign(rng) * U.rr(rng, 0.18, 0.72),
        size: { w: U.rr(rng, 0.2, 0.5), h: U.rr(rng, 0.1, 0.3) },
        z: U.rr(rng, 0.06, 0.22),                    /* zawsze w tle */
      });
      if (f.color === 'white') f.alpha = U.rr(rng, 0.3, 0.55);
      f.ph.distMul *= 0.7;                           /* panele suną łagodniej */
    }

    else if (rel === 'shard') {
      f = baseLight(rng, U.rw(rng, [['spin', 2.2], ['violent', 2.0], ['calm', 1.4]]));
      Object.assign(f, {
        shape: 'tri',
        ...lightColor(rng, [['white', 0.5], ['taupe', 0.5]]),
        xu: anchor.xu + (rng() - 0.5) * 0.44,
        yv: anchor.yv, dyVm: (rng() - 0.5) * 0.34,
        rot: rng() * U.TAU,
        size: { w: U.rr(rng, 0.2, 0.52), h: U.rr(rng, 0.05, 0.14) },
        z: U.rr(rng, 0.5, 0.8),
      });
    }

    else { /* beam — stroma, szeroka smuga światła */
      f = baseLight(rng, 'calm');
      Object.assign(f, {
        shape: 'bar',
        color: 'white', blend: 'source-over', alpha: U.rr(rng, 0.3, 0.55),
        xu: anchor.xu + (rng() - 0.5) * 0.36,
        yv: anchor.yv, dyVm: (rng() - 0.5) * 0.2,
        rot: U.sign(rng) * U.rr(rng, 1.05, 1.42),    /* niemal pionowo */
        size: { w: U.rr(rng, 0.55, 1.05), h: U.rr(rng, 0.05, 0.13) },
        z: U.rr(rng, 0.18, 0.34),
      });
      f.ph.distMul *= 0.8;
    }

    out.push(f);
  }
  return out;
}

/* --- wolne figury lekkie (wypełnienie komórek) --------------- */

export function makeFree(seed, c, freeMax) {
  const rng = U.rngFor(seed, 'free', c);
  const count = Math.floor(rng() * (freeMax + 1));
  const out = [];

  for (let i = 0; i < count; i++) {
    const t = U.rw(rng, [['dot', 3], ['line', 2.6], ['shard', 1.5], ['bar', 1.6]]);
    const pers = U.rw(rng, [['drift', 2], ['jitter', 2], ['violent', 1.6], ['calm', 1.6], ['spin', 1], ['pulse', 1]]);
    const f = baseLight(rng, pers);
    const xu = U.sign(rng) * U.rr(rng, 0.2, 0.62);   /* obrzeża — dalej od osi */
    const yv = c + rng();

    if (t === 'dot') {
      Object.assign(f, {
        shape: 'circle', ...lightColor(rng, [['orange', 1], ['taupe', 1.4], ['black', 0.5]]),
        xu, yv, rot: 0, size: { r: U.rr(rng, 0.018, 0.05) }, z: U.rr(rng, 0.55, 0.9),
      });
    } else if (t === 'line') {
      Object.assign(f, {
        shape: 'line', ...lightColor(rng, [['black', 0.6], ['white', 0.4]]),
        xu, yv, rot: U.sign(rng) * U.rr(rng, 0.25, 0.7),
        size: { w: U.rr(rng, 0.3, 0.75), h: U.rr(rng, 0.0035, 0.0055) },
        z: U.rr(rng, 0.35, 0.85),
      });
    } else if (t === 'shard') {
      Object.assign(f, {
        shape: 'tri', ...lightColor(rng, [['white', 0.5], ['taupe', 0.5]]),
        xu, yv, rot: rng() * U.TAU,
        size: { w: U.rr(rng, 0.16, 0.4), h: U.rr(rng, 0.04, 0.1) },
        z: U.rr(rng, 0.45, 0.75),
      });
    } else {
      Object.assign(f, {
        shape: 'bar', ...lightColor(rng, [['black', 0.5], ['white', 0.3], ['orange', 0.2]]),
        xu, yv, rot: U.sign(rng) * U.rr(rng, 0.15, 0.6),
        size: { w: U.rr(rng, 0.08, 0.22), h: U.rr(rng, 0.02, 0.05) },
        z: U.rr(rng, 0.5, 0.8),
      });
    }
    out.push(f);
  }
  return out;
}

/* ============================================================
   EWALUACJA — figura + stan chwili → transformacja do rysowania
   st: { scroll, t, dt, velMag, hold, mx, my, vhPx, vminPx, cx, cy, vwPx, reduced }
   ============================================================ */

export function evaluate(fig, st) {
  const P = PARAMS;
  const yb = fig.yv - st.scroll + (fig.dyVm * st.vminPx) / st.vhPx; /* pozycja na tablicy [vh] */

  /* twarde cięcie — figura poza zasięgiem podróży */
  if (yb < -0.8 || yb > 2.0) {
    if (fig._glDur) { fig._glDur = 0; fig._glFire = false; }   /* wróci czysta */
    return null;
  }

  let dxVm = 0, dyVh = 0, dyVmIdle = 0, rotOff = 0, scaleMul = 1, alphaMul = 1;
  let evTik = false, evExit = false;
  let glitch = null;
  let mayGlitch = false;
  let wave = null;
  let mayWave = false;

  if (fig.kind === 'light') {

    /* maska gęstości (na żywo, z miękką krawędzią) */
    const dens = P.comp.density;
    if (fig.rank > dens) return null;
    alphaMul *= U.clamp01((dens - fig.rank) / 0.05);
    if (alphaMul <= 0.01) return null;

    /* --- FAZY 1 i 3: wyzwalane scrollem, grane w czasie ------ */
    /* Gdy slot figury mija próg (enterAt / exitAt), animacja
       odgrywa się we własnym tempie figury (inDur / outDur,
       bazowo 0.2–1.5 s) — niezależnie od tempa scrollowania.
       Cofnięcie scrolla za próg odwraca animację. */
    const M = P.phase.margin;
    if (yb >= 1 + M) {                                            /* głęboko pod kadrem */
      fig._inT = 0; fig._outT = 0;
      fig._tikStep = undefined; fig._exitFired = false;            /* wróci jak nowa */
      fig._waveFired = false; fig._settled = false;
      return null;
    }
    if (yb <= -M) {                                               /* daleko nad kadrem */
      fig._inT = 1; fig._outT = 1;
      fig._tikStep = undefined;
      fig._waveFired = false;
      return null;
    }

    if (fig._inT === undefined) {
      /* pierwsze spotkanie (np. start z ?scroll=…) — stan zastany */
      fig._inT  = yb <= P.phase.enterAt ? 1 : 0;
      fig._outT = yb <= P.phase.exitAt  ? 1 : 0;
    }

    const isViolent = fig.pers === 'violent';
    const vio = P.motion.violence * (st.reduced ? 0.25 : 1);

    /* zegary faz (gwałtowne wchodzą jeszcze szybciej przy dużej gwałtowności) */
    const inDur  = Math.max(0.05, fig.inDur  * P.phase.durMulIn * (isViolent ? 1 - 0.4 * vio : 1));
    const outDur = Math.max(0.05, fig.outDur * P.phase.durMulOut);
    fig._inT  = U.clamp01(fig._inT  + (st.dt / inDur)  * (yb <= P.phase.enterAt ? 1 : -1));
    fig._outT = U.clamp01(fig._outT + (st.dt / outDur) * (yb <= P.phase.exitAt  ? 1 : -1));

    /* --- znikanie: wspólne dla obu kierunków scrolla ---------- */
    /* Figura schodzi ze sceny na dwa sposoby, zależnie od tego, w którą
       stronę jedzie tablica:
         w dół  — fazą 3: _outT rośnie 0 → 1, figura wylatuje górą,
         w górę — cofniętą fazą 1: _inT spada 1 → 0, figura chowa się
                  z powrotem dołem (zjeżdża o entryDist i gaśnie).
       Dla oka i dla ucha to samo zniknięcie, więc oba grają ten sam
       dźwięk. Startuje on na PROGU znikania — o cały czas fazy
       (0.2–1.5 s) wcześniej, niż figura naprawdę zgaśnie.

       _settled pilnuje, żeby cofnięta faza 1 nie zabrzmiała figurze,
       która nigdy nie zdążyła wejść: przy starcie z ?scroll=… albo tuż
       po przeskoku sceny _inT bywa zerowe, choć figura nigdy nie była
       na scenie — bez tej flagi każda taka figura witałaby nas
       dźwiękiem zniknięcia. */
    if (fig._inT >= 0.999) fig._settled = true;
    const leaving = fig._outT > 0.02
      || (fig._settled && yb > P.phase.enterAt && fig._inT < 0.98);
    if (leaving) {
      if (!fig._exitFired) { fig._exitFired = true; evExit = true; }
    } else if (fig._outT <= 0.001 && fig._inT >= 0.999) {
      /* figura wróciła na swoje miejsce — następne zejście znów zagra */
      fig._exitFired = false;
    }

    /* --- FAZA 1 · wjazd (0 → 1 we własnym czasie) ------------ */
    if (fig._inT < 1) {
      /* silnie wytracający ease-out (bez przestrzelenia);
         gwałtowne startują ostrzej — wyższa potęga */
      const pow = U.clamp(
        P.phase.easePow * fig.easeJit * (isViolent ? 1 + 0.5 * vio : 1),
        1.5, 8
      );
      const E = U.easeOutPow(fig._inT, pow);
      const back = 1 - E;                       /* 1 → 0: odległość od slotu */
      dyVh  += P.phase.entryDist * fig.ph.distMul * back;
      dxVm  += fig.ph.entryDx * P.phase.entrySpread * back * 3.2;
      rotOff += fig.ph.entryRot * (isViolent ? (1 + vio) : 1) * back;
      alphaMul *= U.clamp01(fig._inT / 0.35);
      scaleMul *= U.lerp(0.82, 1, U.easeOutCubic(Math.min(1, fig._inT * 1.5)));
      if (alphaMul <= 0.01) return null;
    }

    /* --- FAZA 3 · wyjazd (0 → 1 we własnym czasie) ----------- */
    if (fig._outT > 0) {
      const E = U.easeInCubic(fig._outT);
      dyVh  -= P.phase.exitDist * fig.ph.distMul * E;
      dxVm  += fig.ph.exitDx * P.phase.entrySpread * E * 3.2;
      rotOff += fig.ph.exitRot * (isViolent ? (1 + vio * 1.4) : 1) * E;
      alphaMul *= 1 - U.smoothstep(0.55, 1, fig._outT);
      if (alphaMul <= 0.01) return null;
    }

    /* --- FAZA 2 (i wszystkie) · ruch własny ----------------- */
    const feedback = 1 + st.velMag * P.motion.velReact * 1.7;   /* sprzężenie zwrotne */
    const idleAmp = 0.011 * P.motion.idleAmp * fig.idle.amp * feedback * (st.reduced ? 0.3 : 1);
    const sp = P.motion.idleSpeed * fig.idle.speed * (1 - st.hold * 0.6);
    const tt = st.t * sp + fig.seed * 0.61;

    dxVm     += U.wobble(tt * 0.9, fig.seed) * idleAmp;
    dyVmIdle += U.wobble(tt * 0.9 + 37.7, fig.seed + 1) * idleAmp * 0.85;
    rotOff   += U.wobble(tt * 0.55 + 11.3, fig.seed + 2) * 0.06 * P.motion.idleAmp * fig.idle.amp;

    if (isViolent && vio > 0.02) {
      /* skokowe zmiany pozy + krótki wstrząs przy każdym skoku */
      const rate = 0.45 + vio * 1.4;
      const ph = st.t * rate * Math.max(0.2, P.motion.idleSpeed) + fig.seed * 0.13;
      const step = Math.floor(ph);
      const frac = ph - step;
      /* nowy skok = "tiknięcie"; pierwsze spotkanie z figurą jest ciche,
         inaczej wjazd całej kompozycji sypnąłby salwą tików */
      if (fig._tikStep === undefined) fig._tikStep = step;
      else if (fig._tikStep !== step) { fig._tikStep = step; evTik = true; }
      const spike = Math.max(0, 1 - frac / 0.14);
      const j = (n) => (U.hash01(step * 7 + fig.seed + n) - 0.5) * 2;
      const snapAmp = 0.02 * vio * feedback;
      dxVm     += j(1) * snapAmp + spike * U.wobble(st.t * 26, fig.seed + 4) * snapAmp * 1.6;
      dyVmIdle += j(2) * snapAmp * 0.8;
      rotOff   += j(3) * 0.22 * vio + spike * 0.05 * vio;
    }

    if (fig.spinV) rotOff += st.t * fig.spinV * (0.3 + P.motion.idleSpeed * 0.7);
    if (fig.pulse) scaleMul *= 1 + Math.sin(tt * fig.pulse.f * U.TAU) * fig.pulse.amp * P.motion.idleAmp;

    /* --- fala: figura powolna jest na scenie i faluje -------- */
    /* Znacznik stawiamy dopiero po cięciach (patrz niżej) — inaczej
       figura odrzucona przez maskę gęstości albo krawędź kadru
       zabrałaby swoją kolejkę w melodii i nic by z niej nie zabrzmiało.
       Koniec fali to PRÓG znikania (`leaving`, ten sam co dla dźwięku
       exit), a nie jego dopięcie: fala ma milknąć wtedy, gdy figura
       przestaje falować, a zaczyna schodzić ze sceny — w którąkolwiek
       stronę jedzie tablica. */
    if (WAVING[fig.pers] && fig._inT > 0.02 && !leaving) mayWave = true;

  } else {
    /* --- FIGURY CIĘŻKIE: płynnie, z planszą ------------------ */

    /* maska gęstości ciężkich (na żywo, z miękką krawędzią) */
    const hd = P.comp.heavyDensity;
    if (fig.rank > hd) return null;
    alphaMul *= U.clamp01((hd - fig.rank) / 0.04);
    if (alphaMul <= 0.01) return null;

    /* deterministyczne kołysanie sprzężone wyłącznie ze scrollem */
    dxVm += Math.sin(st.scroll * 0.6 + fig.seed * 0.01) * fig.sway;
    rotOff += fig.spin * st.scroll;                       /* powolna reorientacja */
    rotOff += U.wobble(st.t * 0.13, fig.seed) * 0.006;    /* ledwie żywy oddech */

    /* --- glitch dużej tarczy --------------------------------- */
    /* Proces losowy, liczony osobno dla każdej dużej pomarańczy
       widocznej w kadrze: w każdej klatce szansa rate·dt na to,
       że tarcza rozpadnie się na chwilę na pionowe pasy.
       Celowo NIEdeterministyczny — glitch ma zaskakiwać, więc nie
       może wracać w tym samym miejscu tablicy przy tym ziarnie.
       Przy prefers-reduced-motion nie wyzwalamy go wcale. */
    const G = P.glitch;
    if (fig._glDur > 0) {
      fig._glT += st.dt;
      if (fig._glT >= fig._glDur) fig._glDur = 0;
    } else if (
      G.rate > 0 && fig.shape === 'circle' && fig.size.r >= G.minR && !st.reduced
    ) {
      /* tarcza kwalifikuje się do glitcha, ale losujemy dopiero na
         końcu — dopiero po transformacjach wiadomo, gdzie naprawdę
         jest jej środek (patrz niżej) */
      mayGlitch = true;
    }
  }

  /* --- wspólne: oś, parallax, kursor ------------------------ */
  const pull = U.clamp01(P.comp.axisPull + st.hold * 0.34);
  const xEff = fig.xu * (1 - pull * 0.9) + dxVm;

  let sx = st.cx + xEff * st.vminPx;
  let sy = (yb + dyVh) * st.vhPx + dyVmIdle * st.vminPx;

  /* rozwarstwienie: głębia rozsuwa figury względem środka kadru */
  const depthAmt = P.motion.depth * (fig.kind === 'heavy' ? P.motion.heavyDepth : 1);
  sy = st.cy + (sy - st.cy) * (1 + fig.depth * depthAmt);
  sx += st.mx * fig.depth * 30 * P.motion.cursorPar;
  sy += st.my * fig.depth * 14 * P.motion.cursorPar;

  /* skala globalna */
  scaleMul *= P.comp.scale;

  /* cięcie po transformacjach */
  const dim = (fig.size.r ? fig.size.r * 2 : Math.max(fig.size.w, fig.size.h)) * st.vminPx * scaleMul;
  if (sy < -dim * 1.2 || sy > st.vhPx + dim * 1.2) return null;
  if (Math.abs(sx - st.cx) > st.vwPx * 0.5 + dim * 1.2) return null;

  const alpha = fig.alpha * alphaMul;
  if (alpha <= 0.008) return null;

  /* --- wyzwolenie glitcha ----------------------------------- */
  /* Dopiero tutaj znamy prawdziwy środek tarczy na ekranie, więc
     dopiero tutaj wolno losować. Warunek: ŚRODEK musi być w kadrze —
     inaczej rozdarcie łapało koła ledwie zaglądające zza krawędzi
     i z całego efektu widać było sam strzęp. Raz rozpoczęty glitch
     dogrywa się do końca, także gdy tarcza zdąży zjechać z ekranu. */
  if (mayGlitch && sx >= 0 && sx <= st.vwPx && sy >= 0 && sy <= st.vhPx) {
    const G = P.glitch;
    if (Math.random() < G.rate * st.dt) {
      fig._glDur = U.rr(Math.random, Math.min(G.durMin, G.durMax), Math.max(G.durMin, G.durMax));
      fig._glT = 0;
      fig._glSeed = Math.floor(Math.random() * 1e6);
      /* gęstość podziału losowana przy każdym glitchu z osobna — ta sama
         tarcza raz pęka na kilka kafli, raz na gęsty raster */
      fig._glSlices = Math.round(
        U.rr(Math.random, Math.min(G.slicesMin, G.slicesMax), Math.max(G.slicesMin, G.slicesMax))
      );
      fig._glFire = true;                 /* zaczep dźwięku — audio/accents.js */
    }
  }
  if (fig._glDur > 0) {
    glitch = { seed: fig._glSeed, t: fig._glT, dur: fig._glDur, slices: fig._glSlices, fire: fig._glFire };
    fig._glFire = false;
  }

  /* --- fala ------------------------------------------------- */
  /* `fire` tylko przy pierwszym pojawieniu się figury na scenie;
     _waveFired zeruje się dopiero, gdy figura wyjedzie poza zasięg,
     więc scrollowanie w tę i we w tę nie wyzwala jej na nowo. */
  if (mayWave) {
    wave = { fire: !fig._waveFired };
    fig._waveFired = true;
  }

  return {
    fig, x: sx, y: sy,
    rot: fig.rot + rotOff,
    s: scaleMul,
    alpha,
    z: fig.z,
    tik: evTik,
    exit: evExit,
    glitch,
    wave,
  };
}
